import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Download, FileDown, Truck, UserPlus } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { api, formatEUR, downloadAdminPdf, getBrowserTz } from "../../lib/api";
import StatusMessageModal from "../../components/StatusMessageModal";
import AssignTransporterModal from "../../components/AssignTransporterModal";
import DeliveryEvidenceCard from "../../components/DeliveryEvidenceCard";
import {
  SHIPMENT_STATUS_ORDER,
  SHIPMENT_STATUS_LABELS,
  SHIPMENT_STATUS_BADGE_CLASSES,
} from "../../lib/shipment-statuses";

const ASSIGNABLE_STATUSES = new Set([
  "confirmado_pendiente_recogida",
  "recogido",
  "en_transito",
  "en_puerto_almeria",
  "en_puerto_oran",
  "en_aduanas",
  "camino_campamentos",
  "reparto",
  "entregado",
]);

const TYPE_LABELS = {
  ropa: "Ropa",
  alimentos: "Alimentos",
  electronica: "Electrónica",
  tv: "TV",
  medicinas: "Medicinas",
  herramientas: "Herramientas",
  otros: "Otros",
};

const ELECTRONICS_SUBTYPE_LABELS = {
  nevera: "Nevera",
  lavadora: "Lavadora",
  microondas: "Microondas",
  vitroceramica: "Vitrocerámica",
  otros: "Otros aparatos",
};

// Phase 47 — use the shared metadata module. Historical `en_transito`
// keeps rendering (via SHIPMENT_STATUS_LABELS) but is not offered in
// the dropdown anymore.
const STATUS_OPTIONS = SHIPMENT_STATUS_ORDER;
const STATUS_LABELS = SHIPMENT_STATUS_LABELS;
const STATUS_CLASSES = SHIPMENT_STATUS_BADGE_CLASSES;

export function AdminShipments() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [status, setStatus] = useState("all");
  const [zipBusy, setZipBusy] = useState(false);
  // Phase 43 — transporter column + bulk assign + filter
  const [transporters, setTransporters] = useState([]);
  const [transporterFilter, setTransporterFilter] = useState("all"); // "all" | "unassigned" | <id>
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTargets, setAssignTargets] = useState([]); // ids to send to modal

  const load = () => {
    const parts = ["limit=100"];
    if (status !== "all") parts.push(`status=${status}`);
    if (transporterFilter !== "all") parts.push(`transporter_id=${transporterFilter}`);
    return api.get(`/admin/shipments?${parts.join("&")}`).then((r) => {
      setData(r.data);
      // Reset any selection whose row is no longer visible
      setSelectedIds((prev) => {
        const visible = new Set((r.data.items || []).map((i) => i.id));
        const next = new Set([...prev].filter((id) => visible.has(id)));
        return next;
      });
    });
  };

  useEffect(() => {
    load();
  }, [status, transporterFilter]); // eslint-disable-line

  useEffect(() => {
    api
      .get("/admin/transporters", { params: { active_only: false } })
      .then(({ data }) => setTransporters(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const visibleAssignableIds = useMemo(
    () => (data.items || []).filter((s) => ASSIGNABLE_STATUSES.has(s.status)).map((s) => s.id),
    [data.items],
  );
  const allAssignableSelected =
    visibleAssignableIds.length > 0 &&
    visibleAssignableIds.every((id) => selectedIds.has(id));

  const toggleRow = (id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };
  const toggleAll = (checked) => {
    if (checked) setSelectedIds(new Set(visibleAssignableIds));
    else setSelectedIds(new Set());
  };

  const openBulkAssign = () => {
    if (selectedIds.size === 0) {
      toast.error("Selecciona al menos un envío");
      return;
    }
    setAssignTargets([...selectedIds]);
    setAssignOpen(true);
  };

  const openSingleAssign = (id) => {
    setAssignTargets([id]);
    setAssignOpen(true);
  };

  return (
    <div data-testid="admin-shipments" className="space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-3xl font-black tracking-tight flex-1">Envíos</h1>
        <Button
          data-testid="admin-download-shipments-by-status-btn"
          variant="outline"
          onClick={async () => {
            const t = toast.loading("Generando Excel por estado…");
            setZipBusy(true);
            try {
              const today = new Date().toISOString().slice(0, 10);
              const tz = encodeURIComponent(getBrowserTz());
              await downloadAdminPdf(
                `/admin/reports/shipments-by-status.zip?tz=${tz}`,
                `envios_por_estado_${today}.zip`,
              );
              toast.success("ZIP descargado", { id: t });
            } catch (_e) {
              toast.error("No se pudo generar el ZIP", { id: t });
            } finally {
              setZipBusy(false);
            }
          }}
          disabled={zipBusy}
          className="gap-2 border-[#00953B] text-[#00953B] hover:bg-[#F1FAF3] hover:text-[#00753a] rounded-full"
        >
          <FileDown className="h-4 w-4" />
          {zipBusy ? "Generando…" : "Descargar Excel por estado (ZIP)"}
        </Button>
        <Button
          data-testid="admin-envios-pdf-btn"
          variant="outline"
          onClick={async () => {
            const t = toast.loading("Descargando…");
            try {
              const today = new Date().toISOString().slice(0, 10);
              const tz = encodeURIComponent(getBrowserTz());
              await downloadAdminPdf(
                `/admin/reports/shipments-pending.pdf?tz=${tz}`,
                `MercaGo-envios-pendientes-${today}.pdf`,
              );
              toast.success("PDF descargado", { id: t });
            } catch (_e) {
              toast.error("No se pudo generar el PDF", { id: t });
            }
          }}
          className="gap-2 border-[#00953B] text-[#00953B] hover:bg-[#F1FAF3] hover:text-[#00753a] rounded-full"
        >
          <FileDown className="h-4 w-4" /> Descargar PDF pendientes
        </Button>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48" data-testid="admin-shipments-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s} value={s}>
                {STATUS_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={transporterFilter} onValueChange={setTransporterFilter}>
          <SelectTrigger className="w-52" data-testid="admin-shipment-transporter-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los transportistas</SelectItem>
            <SelectItem value="unassigned">Sin asignar</SelectItem>
            {transporters.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}
                {t.is_active === false ? " (inactivo)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedIds.size > 0 && (
        <div
          data-testid="admin-shipment-bulk-bar"
          className="sticky top-0 z-10 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 flex items-center gap-3"
        >
          <span className="text-sm text-emerald-900 font-semibold">
            {selectedIds.size} {selectedIds.size === 1 ? "envío" : "envíos"} seleccionado{selectedIds.size === 1 ? "" : "s"}
          </span>
          <Button
            data-testid="admin-shipment-bulk-assign-btn"
            onClick={openBulkAssign}
            className="ml-auto gap-2 rounded-full bg-[#00953B] hover:bg-[#007a30] text-white"
          >
            <UserPlus className="h-4 w-4" /> Asignar seleccionados a…
          </Button>
          <Button
            variant="outline"
            onClick={() => setSelectedIds(new Set())}
            data-testid="admin-shipment-bulk-clear-btn"
          >
            Limpiar
          </Button>
        </div>
      )}
      <div className="rounded-2xl border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-neutral-500 bg-neutral-50">
            <tr>
              <th className="p-3 w-10">
                <Checkbox
                  checked={allAssignableSelected}
                  onCheckedChange={(v) => toggleAll(!!v)}
                  data-testid="admin-shipment-bulk-checkbox-all"
                  aria-label="Seleccionar todos"
                />
              </th>
              <th className="p-3">Fecha</th>
              <th>Envío</th>
              <th>Remitente</th>
              <th>Destinatario</th>
              <th>Tipo</th>
              <th>Coste</th>
              <th>Estado</th>
              <th>Transportista</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((s) => {
              const canAssign = ASSIGNABLE_STATUSES.has(s.status);
              return (
              <tr
                key={s.id}
                data-testid={`admin-shipment-row-${s.id}`}
                className="border-t"
              >
                <td className="p-3">
                  {canAssign ? (
                    <Checkbox
                      checked={selectedIds.has(s.id)}
                      onCheckedChange={(v) => toggleRow(s.id, !!v)}
                      data-testid={`admin-shipment-bulk-checkbox-${s.id}`}
                    />
                  ) : (
                    <span className="text-neutral-300 text-xs">—</span>
                  )}
                </td>
                <td className="p-3 whitespace-nowrap">
                  {new Date(s.created_at).toLocaleDateString("es-ES")}
                </td>
                <td className="font-mono">#{s.id.slice(0, 8)}</td>
                <td>
                  <div>{s.sender_name}</div>
                  <div className="text-xs text-neutral-500">{s.sender_email}</div>
                </td>
                <td>
                  <div>{s.recipient_name}</div>
                  <div className="text-xs text-neutral-500">{s.recipient_camp}</div>
                </td>
                <td>{TYPE_LABELS[s.shipment_type] || s.shipment_type}</td>
                <td className="font-semibold">{formatEUR(s.fee_eur)}</td>
                <td>
                  <Badge className={STATUS_CLASSES[s.status] || "bg-neutral-200"}>
                    {STATUS_LABELS[s.status] || s.status}
                  </Badge>
                </td>
                <td>
                  {s.transporter_name ? (
                    <button
                      type="button"
                      onClick={() => canAssign && openSingleAssign(s.id)}
                      disabled={!canAssign}
                      data-testid={`admin-shipment-assign-btn-${s.id}`}
                      className={`inline-flex items-center gap-1 text-xs font-semibold ${
                        canAssign
                          ? "text-[#00953B] hover:underline"
                          : "text-neutral-500 cursor-default"
                      }`}
                      title={canAssign ? "Reasignar" : "Estado no editable"}
                    >
                      <Truck className="h-3.5 w-3.5" />
                      {s.transporter_name}
                    </button>
                  ) : canAssign ? (
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`admin-shipment-assign-btn-${s.id}`}
                      onClick={() => openSingleAssign(s.id)}
                      className="gap-1 text-xs"
                    >
                      <UserPlus className="h-3.5 w-3.5" /> Asignar
                    </Button>
                  ) : (
                    <span className="text-neutral-300">—</span>
                  )}
                </td>
                <td>
                  <Link
                    to={`/admin/envios/${s.id}`}
                    className="text-[#00953B] font-semibold"
                  >
                    Ver
                  </Link>
                </td>
              </tr>
              );
            })}
            {data.items.length === 0 && (
              <tr>
                <td colSpan={10} className="p-6 text-center text-neutral-500 text-sm">
                  No hay envíos con este filtro.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <AssignTransporterModal
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        kind="shipments"
        itemIds={assignTargets}
        onAssigned={() => {
          setSelectedIds(new Set());
          load();
        }}
      />
    </div>
  );
}

export function AdminShipmentDetail() {
  const { id } = useParams();
  const [shipment, setShipment] = useState(null);
  const [waPrompt, setWaPrompt] = useState(null); // { status } after successful PATCH
  const navigate = useNavigate();

  const load = () => api.get(`/admin/shipments/${id}`).then((r) => setShipment(r.data));

  useEffect(() => {
    load();
  }, [id]); // eslint-disable-line

  const changeStatus = async (v) => {
    try {
      await api.patch(`/admin/shipments/${id}/status`, { status: v });
      toast.success("Estado actualizado");
      load();
      // Phase 22 — offer to draft & send a WhatsApp notification to the sender.
      setWaPrompt({ status: v });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const downloadPdf = async () => {
    try {
      const resp = await api.get(
        `/shipments/${id}/receipt.pdf?tz=${encodeURIComponent(getBrowserTz())}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(
        new Blob([resp.data], { type: "application/pdf" })
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `MercaGo-envio-${id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar el recibo");
    }
  };

  if (!shipment) return <div className="h-64 bg-neutral-100 rounded animate-pulse" />;

  return (
    <div data-testid="admin-shipment-detail" className="space-y-4 max-w-3xl">
      <Link to="/admin/envios" className="text-sm text-neutral-500">
        ← Volver
      </Link>
      <h1 className="text-3xl font-black tracking-tight">
        Envío #{shipment.id.slice(0, 8)}
      </h1>
      <div className="rounded-2xl border bg-white p-5 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm text-neutral-500">Estado:</span>
          <Select value={shipment.status} onValueChange={changeStatus}>
            <SelectTrigger className="w-48" data-testid="admin-shipment-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>
                  {STATUS_LABELS[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={downloadPdf}
            variant="outline"
            data-testid="admin-shipment-pdf-btn"
            className="gap-2"
          >
            <Download className="h-4 w-4" /> Descargar PDF
          </Button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 pt-2">
          <div>
            <div className="text-xs text-neutral-500 mb-1">Remitente</div>
            <div className="font-semibold">{shipment.sender_name}</div>
            <div className="text-sm text-neutral-600">{shipment.sender_email}</div>
            <div className="text-sm text-neutral-600">{shipment.sender_phone}</div>
          </div>
          <div>
            <div className="text-xs text-neutral-500 mb-1">Destinatario</div>
            <div className="font-semibold">{shipment.recipient_name}</div>
            <div className="text-sm text-neutral-600">{shipment.recipient_phone}</div>
            <div className="text-sm text-neutral-600">
              {shipment.recipient_camp} · {shipment.recipient_daira} ·{" "}
              {shipment.recipient_barrio}
            </div>
          </div>
        </div>

        <div className="border-t pt-3 text-sm grid grid-cols-2 gap-2">
          <div>
            <span className="text-neutral-500">Tipo:</span>{" "}
            {TYPE_LABELS[shipment.shipment_type] || shipment.shipment_type}
            {shipment.shipment_type === "electronica" && shipment.electronics_subtype && (
              <span
                data-testid="admin-shipment-subtype"
                className="ml-2 inline-flex items-center rounded-full bg-indigo-50 text-indigo-700 px-2 py-0.5 text-xs font-semibold border border-indigo-100"
              >
                {ELECTRONICS_SUBTYPE_LABELS[shipment.electronics_subtype] || shipment.electronics_subtype}
              </span>
            )}
          </div>
          <div>
            <span className="text-neutral-500">
              {shipment.shipment_type === "tv" ? "Pulgadas:" : "Peso:"}
            </span>{" "}
            {shipment.shipment_type === "tv"
              ? `${shipment.tv_inches}"`
              : `${shipment.weight_kg} kg`}
          </div>
          <div className="col-span-2">
            <span className="text-neutral-500">Descripción:</span>{" "}
            <span className="whitespace-pre-wrap">{shipment.description}</span>
          </div>
        </div>

        <div className="border-t pt-2 text-sm">
          <div className="flex justify-between font-bold">
            <span>Total</span>
            <span>{formatEUR(shipment.fee_eur)}</span>
          </div>
        </div>
      </div>
      {waPrompt && (
        <StatusMessageModal
          kind="shipment"
          entityId={shipment.id}
          status={waPrompt.status}
          phone={shipment.sender_phone || ""}
          onClose={() => setWaPrompt(null)}
        />
      )}
      {shipment.status === "entregado" && (
        <DeliveryEvidenceCard shipmentId={shipment.id} />
      )}
    </div>
  );
}
